import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Loader2, Lock, ArrowRight, ShieldCheck } from 'lucide-react';
import { Container, SectionHeading, Btn } from '../components/ui';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { signupCheckout, signupFree } from '../lib/marketingApi';
import { cn } from '@/lib/utils';

const PLANS = [
  {
    key: 'free', name: 'Free', price: 'Free', per: 'forever',
    blurb: '5 free bill credits every month — no card required.',
    features: ['AI bill acquisition & parsing', 'Portal Agents', 'Validation & exceptions', 'Standard dashboards'],
  },
  {
    key: 'bp_starter_monthly', name: 'Starter', price: '$250', per: '/mo',
    blurb: '125 bill credits included, then $2.00 per additional account.',
    features: ['Up to a few hundred accounts', 'AI collection & parsing', 'Bulk ACH payment files', 'Email support'],
  },
  {
    key: 'bp_growth_monthly', name: 'Growth', price: '$6,000', per: '/mo', highlight: true,
    blurb: '4,000 bill credits included, then $1.50 per additional account.',
    features: ['Up to 4,000 accounts / month', 'Everything in Starter', 'Priority processing SLAs', 'Managed-service options'],
  },
];

const inputBase = 'h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-white/15 dark:bg-white/[0.04] dark:text-white';

export default function Signup() {
  const params = new URLSearchParams(useLocation().search);
  const navigate = useNavigate();
  const preselect = params.get('plan');
  const [selected, setSelected] = useState(PLANS.some((p) => p.key === preselect) ? preselect : 'bp_growth_monthly');
  const [form, setForm] = useState({ name: '', email: '', company: '' });
  const [loading, setLoading] = useState(false);
  const isFree = selected === 'free';

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!['name', 'email', 'company'].every((k) => String(form[k] || '').trim())) {
      toast.error('Please complete all fields.');
      return;
    }
    setLoading(true);
    try {
      if (isFree) {
        await signupFree({ name: form.name, email: form.email, company: form.company });
        navigate(`/signup/success?free=1&company=${encodeURIComponent(form.company)}`);
        return;
      }
      const { checkout_url } = await signupCheckout({
        lookup_key: selected,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
        company: form.company,
      });
      if (checkout_url) window.location.href = checkout_url;
      else throw new Error('no url');
    } catch (err) {
      toast.error(isFree ? 'Could not create your account. Please try again.' : 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div data-testid="signup-page">
      <Seo title="Start Your BillPortal Subscription" description="Create your BillPortal account and start with the plan that fits your portfolio. Secure checkout powered by Stripe." noindex />
      <section className="border-b border-slate-200 bg-slate-50 px-5 py-12 dark:border-white/10 dark:bg-white/[0.02] sm:px-8 sm:py-16">
        <Container>
          <SectionHeading eyebrow="Get started" title="Create your BillPortal account" subtitle="Pick a plan, tell us who you are, and complete secure checkout. Your account is created automatically once payment succeeds." />
        </Container>
      </section>

      <section className="px-5 py-14 sm:px-8 sm:py-16">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            {/* Plan selection */}
            <Reveal>
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">1 · Choose your plan</div>
              <div className="mt-4 space-y-4">
                {PLANS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setSelected(p.key)}
                    data-testid={`signup-plan-${p.name.toLowerCase()}`}
                    className={cn(
                      'w-full rounded-2xl border p-5 text-left transition-all',
                      selected === p.key ? 'border-brand ring-2 ring-brand/20' : 'border-slate-200 hover:border-brand/40 dark:border-white/10'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={cn('flex h-5 w-5 items-center justify-center rounded-full border-2', selected === p.key ? 'border-brand bg-brand text-white' : 'border-slate-300')}>
                          {selected === p.key && <Check className="h-3 w-3" />}
                        </span>
                        <span className="font-display text-lg font-bold text-slate-900 dark:text-white">{p.name}</span>
                        {p.highlight && <span className="rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-bold uppercase text-brand">Popular</span>}
                      </div>
                      <div className="text-right"><span className="font-display text-xl font-black text-slate-900 dark:text-white">{p.price}</span><span className="text-xs text-slate-400">{p.per}</span></div>
                    </div>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{p.blurb}</p>
                    <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300"><Check className="h-3.5 w-3.5 text-brand" /> {f}</li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-400">Prices in USD. Recurring monthly subscription — cancel anytime. Bill & AI credits are managed inside the app.</p>
            </Reveal>

            {/* Details + checkout */}
            <Reveal delay={80}>
              <form onSubmit={onSubmit} noValidate className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/[0.03] sm:p-7">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">2 · Your details</div>
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="su-name">Full name</label>
                    <input id="su-name" data-testid="signup-name" className={inputBase} value={form.name} onChange={set('name')} placeholder="Jane Doe" required />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="su-email">Work email</label>
                    <input id="su-email" data-testid="signup-email" type="email" className={inputBase} value={form.email} onChange={set('email')} placeholder="jane@company.com" required />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200" htmlFor="su-company">Company / organisation</label>
                    <input id="su-company" data-testid="signup-company" className={inputBase} value={form.company} onChange={set('company')} placeholder="Acme Property Group" required />
                  </div>
                </div>

                <Btn type="submit" size="lg" full disabled={loading} className="mt-6" data-testid="signup-checkout-btn">
                  {loading
                    ? (<><Loader2 className="h-4 w-4 animate-spin" /> {isFree ? 'Creating your account…' : 'Redirecting to secure checkout…'}</>)
                    : (<>{isFree ? 'Create free account' : 'Continue to secure checkout'} <ArrowRight className="h-4 w-4" /></>)}
                </Btn>
                <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-400">
                  {isFree ? (
                    <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> No card required — 5 free bill credits every month</span>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Powered by Stripe</span>
                      <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Card details never touch our servers</span>
                    </>
                  )}
                </div>
              </form>
            </Reveal>
          </div>
        </Container>
      </section>
    </div>
  );
}
